import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classnames from 'classnames';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import {
  Dropdown,
  DropdownOptionLabel,
} from '#src/atoms/dropdowns/Dropdown/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconBell } from '#src/atoms/icons/system/iconBell.ts';
import { iconBellOn } from '#src/atoms/icons/system/iconBellOn.ts';
import { iconBellSlash } from '#src/atoms/icons/system/iconBellSlash.ts';
import { Checkbox } from '#src/atoms/inputs/Checkbox/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { ScarcityBall } from '#src/components/card/ScarcityBall/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { useUpdateSubscription } from '#src/hooks/subscriptions/useUpdateSubscription.ts';
import type { BlockchainScarcity } from '#src/lib/cards.ts';
import {
  subscribableRarities as so5SubscribableRarities,
  usSportSubscribableRarities,
} from '#src/lib/cards.ts';

import type { NotificationPreference_subscription } from './__generated__/index.graphql.ts';

const bell = (subscribableRarities: string[], notifyForRarities: string[]) => {
  if (notifyForRarities.length === 0) {
    return iconBellSlash;
  }
  if (subscribableRarities.every(r => notifyForRarities.includes(r))) {
    return iconBellOn;
  }
  return iconBell;
};

const messages = defineMessages({
  dialogTitle: {
    defaultMessage: 'Scarcities you want to be notified for',
  },
});

const CustomRoot = styled.div`
  display: flex;
  justify-content: space-between;
`;

type CustomScarcityPreferenceProps = {
  scarcity: BlockchainScarcity;
  enabled: boolean;
  toggle: () => void;
};
const CustomScarcityPreference = ({
  scarcity,
  enabled,
  toggle,
}: CustomScarcityPreferenceProps) => {
  return (
    <CustomRoot>
      <ScarcityBall scarcity={scarcity} />
      <Checkbox checked={enabled} onChange={toggle} />
    </CustomRoot>
  );
};

const Body = styled.div`
  padding: var(--triple-unit);
`;
const DialogContent = styled.div`
  margin-top: var(--double-unit);
  & > * + * {
    margin-top: var(--double-unit);
    border-top: 1px solid var(--c-nd-100);
    padding-top: var(--double-unit);
  }
`;

type Props = {
  subscription: NotificationPreference_subscription;
};

const subscribableRaritiesByType: Record<string, BlockchainScarcity[]> = {
  Player: so5SubscribableRarities as BlockchainScarcity[],
  NBAPlayer: usSportSubscribableRarities as BlockchainScarcity[],
  BaseballPlayer: usSportSubscribableRarities as BlockchainScarcity[],
};

export const NotificationPreference = ({ subscription }: Props) => {
  const [customOpened, setCustomOpened] = useState(false);
  const [open, setOpen] = useState(false);
  const updateSubscription = useUpdateSubscription(subscription);
  const { notifyForRarities } = subscription.preferences;

  const updateSubscriptionRarities = async (
    scarcities: BlockchainScarcity[],
    updatedValue: boolean
  ) => {
    await updateSubscription(scarcities, updatedValue);
  };

  const subscribableRarities =
    subscribableRaritiesByType[subscription.anySubscribable.__typename] || [];

  return (
    <>
      <Dialog
        maxWidth="sm"
        fullWidth
        open={customOpened}
        onClose={() => {
          setCustomOpened(false);
          setOpen(false);
        }}
        title={
          <BodyM bold className="text-center">
            <FormattedMessage {...messages.dialogTitle} />
          </BodyM>
        }
      >
        <Body>
          <BodyM>
            <FormattedMessage defaultMessage="Each time a new Card is listed you’ll receive a notification" />
          </BodyM>
          <DialogContent>
            {subscribableRarities.map(scarcity => {
              const enabled = notifyForRarities.includes(scarcity);
              return (
                <CustomScarcityPreference
                  key={scarcity}
                  scarcity={scarcity}
                  enabled={enabled}
                  toggle={() => {
                    updateSubscriptionRarities([scarcity], !enabled);
                  }}
                />
              );
            })}
          </DialogContent>
        </Body>
      </Dialog>
      <Dropdown
        label={props => (
          <IconButton
            size="medium"
            icon={bell(subscribableRarities, notifyForRarities)}
            color="tertiary"
            disableDebounce
            {...props}
          />
        )}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        open={open}
      >
        <DropdownOptionLabel
          className={classnames({
            selected: subscribableRarities.every(r =>
              notifyForRarities.includes(r)
            ),
          })}
          onClick={async () => {
            await updateSubscriptionRarities(subscribableRarities, true);
            setOpen(false);
          }}
        >
          <FontAwesomeIcon icon={iconBellOn} />
          <FormattedMessage defaultMessage="All scarcities" />
        </DropdownOptionLabel>
        <DropdownOptionLabel
          className={classnames({
            selected:
              notifyForRarities.length > 0 &&
              notifyForRarities.length < subscribableRarities.length,
          })}
          onClick={() => setCustomOpened(true)}
        >
          <FontAwesomeIcon icon={iconBell} />
          <FormattedMessage defaultMessage="Custom" />
        </DropdownOptionLabel>
        <DropdownOptionLabel
          className={classnames({
            selected: notifyForRarities.length === 0,
          })}
          onClick={() => {
            updateSubscriptionRarities(subscribableRarities, false);
          }}
        >
          <FontAwesomeIcon icon={iconBellSlash} />
          <FormattedMessage defaultMessage="None" />
        </DropdownOptionLabel>
      </Dropdown>
    </>
  );
};

NotificationPreference.fragments = {
  subscription: gql`
    fragment NotificationPreference_subscription on EmailSubscription {
      slug
      preferences {
        slug
        notifyForRarities
      }
      anySubscribable {
        slug
      }
    }
  ` as TypedDocumentNode<NotificationPreference_subscription>,
};
